import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Tabs,
    Tab,
    makeStyles,
    withStyles,
} from '@material-ui/core';
import { Button as AntButton } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import moment from 'moment';
import XLSX from 'xlsx';
import HttpService from '../../services/httpService';
import { setLoading, setSnackInfo } from '../../redux/actions/appAction';
import { setSnagProjects } from '../../redux/actions/snagDetailsAction';
import PanelDownloadProgressDialog from '../../components/PanelDownloadProgressDialog';

const NAVY = '#1C375D';
const ORANGE = '#F47E46';
const GREEN = '#355025';
const FETCH_NEW_DATA_COOLDOWN_MS = 3 * 60 * 1000;
const FETCH_NEW_DATA_COOLDOWN_SECONDS = 180;
const FETCH_NEW_DATA_DISABLE_UNTIL_KEY = 'snagDetails_fetchNewData_disableUntil';
const SNAG_TAB_MODULAR = 'modular';
const SNAG_TAB_HARDWARE = 'hardware';

const HARDWARE_QUEUE_COLUMNS = [
    { key: 'cityOps', label: 'City Ops Manager', bucket: 'cityOps' },
    { key: 'centralDirector', label: 'Center Assistant Director', bucket: 'centralDirector' },
    { key: 'finance', label: 'Finance', bucket: 'finance' },
    { key: 'store', label: 'Store', bucket: 'store' },
];

/** API list uses cityOpsManager / centralAssistantDirector; table columns use cityOps / centralDirector. */
const normalizeHardwareProjectRow = (row = {}) => ({
    ...row,
    createdAt: row.createdDate || row.createdAt,
    cityOps: Number(row.cityOps ?? row.cityOpsManager ?? 0),
    centralDirector: Number(row.centralDirector ?? row.centralAssistantDirector ?? 0),
    finance: Number(row.finance ?? 0),
    store: Number(row.store ?? 0),
});

const classifyHardwareItemBucket = (item = {}) => {
    if (!item || item.status === 'Outwarded' || item.status === 'Rejected') return null;
    if (item.status === 'Ready for Outward') return 'store';
    if (item.status === 'Pending Approval') {
        const stage = item.approvalStage || 'som';
        if (stage === 'som') return 'cityOps';
        if (stage === 'central_director') return 'centralDirector';
        if (stage === 'finance') return 'finance';
    }
    return null;
};

const getHardwareStageLabel = (bucket = '') => {
    const col = HARDWARE_QUEUE_COLUMNS.find((c) => c.bucket === bucket);
    return col?.label || bucket;
};

const resolveHardwareItemAssignee = (item = {}, snag = {}, bucket = '') => {
    const snapshotByBucket = {
        cityOps: item.assignedToCityOpsManager,
        centralDirector: item.assignedToCentralDirector,
        finance: item.assignedToFinanceManager,
        store: item.assignedToDefaultAssignee,
    };
    const snapshot = snapshotByBucket[bucket];
    if (snapshot?.name) return snapshot.name;
    if (snapshot?.email) return snapshot.email;
    if (snag.assignedTo && snag.assignedTo !== '—') return snag.assignedTo;
    if (snag.raiseByName) return snag.raiseByName;
    return '—';
};

const filterSnagsByCreatedDate = (snags, startDate, endDate) => {
    const { startDate: resolvedStartDate, endDate: resolvedEndDate } = resolveDateRange(startDate, endDate);
    const rangeStart = moment(resolvedStartDate).startOf('day');
    const rangeEnd = moment(resolvedEndDate).endOf('day');

    return (snags || []).filter((snag) => {
        const created = moment(snag.createdAt || snag.createdDate);
        return created.isValid() && created.isBetween(rangeStart, rangeEnd, null, '[]');
    });
};
const PANEL_PREVIEW_LIMIT = 500;
const DEFAULT_DATE_RANGE_DAYS = 30;

const getDefaultDateRange = () => ({
    startDate: moment().subtract(DEFAULT_DATE_RANGE_DAYS, 'days').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
});

const resolveDateRange = (startDate, endDate) => {
    const defaults = getDefaultDateRange();
    return {
        startDate: startDate || defaults.startDate,
        endDate: endDate || defaults.endDate,
    };
};

const filterRowsByCreatedDate = (data, startDate, endDate) => {
    const { startDate: resolvedStartDate, endDate: resolvedEndDate } = resolveDateRange(startDate, endDate);
    const rangeStart = moment(resolvedStartDate).startOf('day');
    const rangeEnd = moment(resolvedEndDate).endOf('day');

    return (data || []).filter((row) => {
        const created = moment(row.createdAt || row.createdDate);
        return created.isValid() && created.isBetween(rangeStart, rangeEnd, null, '[]');
    });
};

const STAGE_COLUMNS = [
    { key: 'beamSaw', label: 'Beam Saw', stage: 'Beam saw', getCount: (row) => stageValue(row, 'Beam saw') },
    { key: 'edgeBanding', label: 'Edge Banding', stage: 'Edge banding', getCount: (row) => stageValue(row, 'Edge banding') },
    { key: 'cnc', label: 'CNC', stage: 'CNC', getCount: (row) => stageValue(row, 'CNC') },
    { key: 'aluPanel', label: 'Alu Panel', stage: 'Alu Panels', getCount: (row) => stageValue(row, 'Alu Panels') },
    { key: 'cncJali', label: 'CNC Jali', stage: 'CNC Jali', getCount: (row) => stageValue(row, 'CNC Jali') },
    { key: 'customPanel', label: 'Custom Panel', stage: 'Custom Panels', getCount: (row) => stageValue(row, 'Custom Panels') },
    { key: 'qc', label: 'QC', stage: 'Assembly', getCount: (row) => stageValue(row, 'Assembly') },
    { key: 'packaging', label: 'Packaging', stage: 'Packaging', getCount: (row) => stageValue(row, 'Packaging') },
    { key: 'readyForDispatch', label: 'Ready for Dispatch', stage: 'Completed', getCount: (row) => stageValue(row, 'Completed') },
    { key: 'deliveredProductPanel', label: 'Dispatched', stage: 'Delivered', getCount: (row) => stageValue(row, 'Delivered') },
    { key: 'totalPanels', label: 'Total Panels', stage: 'Total', getCount: (row) => Number(row.totalPanels || 0) },
];

const PANEL_DISPATCH_STAGES = new Set(['Delivered', 'Total']);

const PANEL_BASE_COLUMNS = [
    { key: 'sn', label: 'SN' },
    { key: 'barcode', label: 'Barcode' },
    { key: 'name1', label: 'Panel Name' },
    { key: 'matname', label: 'Material' },
    { key: 'cp90name', label: 'Item' },
    { key: 'stage', label: 'Stage' },
    { key: 'space_type', label: 'Space Type' },
    { key: 'rleng', label: 'Length' },
    { key: 'rwidth', label: 'Width' },
    { key: 'rthk', label: 'Thickness' },
];

const PANEL_DISPATCH_COLUMNS = [
    { key: 'deliveryVehicleNumber', label: 'Vehicle / Courier Number' },
    { key: 'deliveryDate', label: 'Dispatch Date' },
];

const getPanelColumnsForStage = (stage) => (
    PANEL_DISPATCH_STAGES.has(stage)
        ? [...PANEL_BASE_COLUMNS, ...PANEL_DISPATCH_COLUMNS]
        : PANEL_BASE_COLUMNS
);

const StyledTableCell = withStyles(() => ({
    head: {
        backgroundColor: NAVY,
        color: '#FFF2EB',
        fontWeight: 600,
        textAlign: 'center',
        whiteSpace: 'nowrap',
        border: '1px solid #eee5e5',
        padding: '10px 12px',
        fontSize: 13,
    },
    body: {
        fontSize: 13,
        color: NAVY,
        textAlign: 'center',
        border: '1px solid #eee5e5',
        padding: '8px 12px',
        backgroundColor: '#fff',
    },
}))(TableCell);

const useStyles = makeStyles((theme) => ({
    root: { padding: theme.spacing(2) },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
        marginBottom: theme.spacing(2),
    },
    title: {
        color: NAVY,
        fontWeight: 700,
    },
    search: { minWidth: 280 },
    dateField: {
        width: 155,
    },
    actionBtn: {
        backgroundColor: ORANGE,
        color: '#fff',
        fontWeight: 600,
        textTransform: 'none',
        '&:hover': {
            backgroundColor: '#E0663D',
        },
    },
    disabledActionBtn: {
        backgroundColor: '#cccccc',
        color: '#fff',
        fontWeight: 600,
        textTransform: 'none',
        '&:hover': {
            backgroundColor: '#cccccc',
        },
    },
    downloadBtn: {
        backgroundColor: GREEN,
        color: '#fff',
        fontWeight: 600,
        textTransform: 'none',
        '&:hover': {
            backgroundColor: '#2a401e',
        },
    },
    tableContainer: {
        marginTop: theme.spacing(1),
        overflowX: 'auto',
    },
    table: {
        minWidth: 900,
    },
    totalRow: {
        '& td': {
            fontWeight: 700,
            backgroundColor: '#f5f7fa',
        },
    },
    clickableCount: {
        color: NAVY,
        fontWeight: 700,
        textDecoration: 'underline',
        cursor: 'pointer',
        '&:hover': {
            color: ORANGE,
        },
    },
    disabledCount: {
        color: '#9e9e9e',
    },
    dialogTitle: {
        backgroundColor: NAVY,
        color: '#fff',
    },
    dialogMeta: {
        marginBottom: theme.spacing(1.5),
        color: NAVY,
    },
    tabsRoot: {
        marginBottom: theme.spacing(2),
        borderBottom: `1px solid ${NAVY}22`,
    },
    tab: {
        textTransform: 'none',
        fontWeight: 600,
        color: NAVY,
    },
}));

const stageValue = (row, key) => Number(row?.stageWise?.[key] || 0);

const formatPanelStageDisplay = (stage, panel) => {
    if (panel?.isDelivered) return 'Delivered';
    if (!stage) return '';
    if (stage === 'Assembly') return 'QC';
    return stage;
};

const formatCreatedAt = (value) => {
    if (!value) return '—';
    const formatted = moment(value);
    return formatted.isValid() ? formatted.format('DD-MM-YYYY') : '—';
};

const formatDispatchDate = (value) => {
    if (!value) return '—';
    const formatted = moment(value);
    return formatted.isValid() ? formatted.format('DD-MM-YYYY HH:mm') : '—';
};

const buildSnagPanelPayload = (basePayload, activeSearch, searchMode) => {
    const payload = { ...basePayload };
    const searchText = String(activeSearch || '').trim();
    if (searchText && searchMode === 'barcode') {
        payload.barcode = searchText;
    }
    return payload;
};

const getPanelCellValue = (panel, col, index) => {
    switch (col.key) {
        case 'sn':
            return index + 1;
        case 'stage':
            return formatPanelStageDisplay(panel.stage, panel) || '—';
        case 'deliveryDate':
            return formatDispatchDate(panel.deliveryDate);
        case 'deliveryVehicleNumber':
            return panel.deliveryVehicleNumber || '—';
        default:
            return panel[col.key] ?? '—';
    }
};

const mapSnagPanelToExportRow = (panel, index, columns) => {
    const row = {};
    columns.forEach(({ key, label }) => {
        row[label] = getPanelCellValue(panel, { key, label }, index);
    });
    return row;
};

const exportSnagPanelsToCsv = (panels, fileName, stage) => {
    const columns = getPanelColumnsForStage(stage);
    const exportRows = panels.map((panel, index) => mapSnagPanelToExportRow(panel, index, columns));
    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Panels');
    XLSX.writeFile(workbook, fileName, { bookType: 'csv' });
};

const SnagDetails = (props) => {
    const classes = useStyles();
    const initialDateRange = getDefaultDateRange();
    const [activeTab, setActiveTab] = useState(SNAG_TAB_MODULAR);
    const [rows, setRows] = useState([]);
    const [hardwareRows, setHardwareRows] = useState([]);
    const [search, setSearch] = useState('');
    const [activeSearch, setActiveSearch] = useState('');
    const [activeSearchMode, setActiveSearchMode] = useState(null);
    const [startDate, setStartDate] = useState(initialDateRange.startDate);
    const [endDate, setEndDate] = useState(initialDateRange.endDate);
    const [appliedStartDate, setAppliedStartDate] = useState(initialDateRange.startDate);
    const [appliedEndDate, setAppliedEndDate] = useState(initialDateRange.endDate);
    const [isFetchDisabled, setIsFetchDisabled] = useState(false);
    const [fetchTimeLeft, setFetchTimeLeft] = useState(0);
    const modularRequestId = useRef(0);
    const hardwareRequestId = useRef(0);
    const pageLoadingCountRef = useRef(0);

    const beginPageLoading = () => {
        pageLoadingCountRef.current += 1;
        if (pageLoadingCountRef.current === 1) {
            props.setLoading(true);
        }
    };

    const endPageLoading = () => {
        pageLoadingCountRef.current = Math.max(0, pageLoadingCountRef.current - 1);
        if (pageLoadingCountRef.current === 0) {
            props.setLoading(false);
        }
    };
    const [panelDialogOpen, setPanelDialogOpen] = useState(false);
    const [panelDownloadOpen, setPanelDownloadOpen] = useState(false);
    const [panelDownloadProgress, setPanelDownloadProgress] = useState(0);
    const [panelRows, setPanelRows] = useState([]);
    const [panelMeta, setPanelMeta] = useState({
        projectNo: '',
        stageLabel: '',
        stage: '',
        count: 0,
        previewCount: 0,
        isPreview: false,
        projectId: '',
        projectIds: [],
    });
    const [hardwareDialogOpen, setHardwareDialogOpen] = useState(false);
    const [hardwareDetailRows, setHardwareDetailRows] = useState([]);
    const [hardwareDialogMeta, setHardwareDialogMeta] = useState({
        projectNo: '',
        customerName: '',
        city: '',
        stageLabel: '',
        bucket: '',
    });

    const fetchModularData = async (
        searchText = '',
        { startDate: fetchStartDate, endDate: fetchEndDate, updateCache = false } = {}
    ) => {
        const { startDate: resolvedStartDate, endDate: resolvedEndDate } = resolveDateRange(
            fetchStartDate ?? appliedStartDate,
            fetchEndDate ?? appliedEndDate
        );
        const requestId = ++modularRequestId.current;
        beginPageLoading();
        try {
            const res = await HttpService.getSnagProjects({
                search: searchText,
                startDate: resolvedStartDate,
                endDate: resolvedEndDate,
            });
            if (requestId !== modularRequestId.current) return;
            const apiRows = res.data?.msg || [];
            // Search responses are already filtered by API; avoid client re-filter dropping rows
            const data = searchText
                ? apiRows
                : filterRowsByCreatedDate(apiRows, resolvedStartDate, resolvedEndDate);
            setRows(data);
            setActiveSearchMode(res.data?.searchMode || null);
            if (updateCache && !searchText) {
                props.setSnagProjects(apiRows);
            }
            if (searchText && !data.length && activeTab === SNAG_TAB_MODULAR) {
                props.setSnackInfo('Snag not Exist', 'warning');
            }
        } catch (error) {
            if (requestId !== modularRequestId.current) return;
            setRows([]);
            props.setSnackInfo(
                error?.response?.data?.message || error?.response?.data?.error || 'Failed to load snag projects',
                'error'
            );
        } finally {
            endPageLoading();
        }
    };

    const fetchHardwareData = async (
        searchText = '',
        { startDate: fetchStartDate, endDate: fetchEndDate } = {}
    ) => {
        const { startDate: resolvedStartDate, endDate: resolvedEndDate } = resolveDateRange(
            fetchStartDate ?? appliedStartDate,
            fetchEndDate ?? appliedEndDate
        );
        const requestId = ++hardwareRequestId.current;
        beginPageLoading();
        try {
            const res = await HttpService.getHardwareSnagProjects({
                search: searchText || undefined,
                startDate: resolvedStartDate,
                endDate: resolvedEndDate,
            });
            if (requestId !== hardwareRequestId.current) return;
            const projects = (res.data?.projects || []).map(normalizeHardwareProjectRow);
            const filtered = searchText
                ? projects
                : filterRowsByCreatedDate(projects, resolvedStartDate, resolvedEndDate);
            setHardwareRows(filtered);
            if (searchText && !filtered.length && activeTab === SNAG_TAB_HARDWARE) {
                props.setSnackInfo('Snag not Exist', 'warning');
            }
        } catch (error) {
            if (requestId !== hardwareRequestId.current) return;
            setHardwareRows([]);
            props.setSnackInfo(
                error?.response?.data?.message || 'Failed to load hardware snag projects',
                'error'
            );
        } finally {
            endPageLoading();
        }
    };

    const fetchBothTabData = (searchText, range, { updateModularCache = false } = {}) => {
        fetchModularData(searchText, { ...range, updateCache: updateModularCache });
        fetchHardwareData(searchText, range);
    };

    useEffect(() => {
        if (props.isSnagProjectsLoaded) {
            setRows(filterRowsByCreatedDate(props.snagProjects, appliedStartDate, appliedEndDate));
        } else {
            fetchModularData('', {
                startDate: initialDateRange.startDate,
                endDate: initialDateRange.endDate,
                updateCache: true,
            });
        }
        fetchHardwareData('', {
            startDate: initialDateRange.startDate,
            endDate: initialDateRange.endDate,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const disableUntilTimestamp = localStorage.getItem(FETCH_NEW_DATA_DISABLE_UNTIL_KEY);

        if (disableUntilTimestamp) {
            const now = Date.now();
            const disableUntil = parseInt(disableUntilTimestamp, 10);

            if (disableUntil > now) {
                const remainingTimeInSeconds = Math.ceil((disableUntil - now) / 1000);
                setFetchTimeLeft(remainingTimeInSeconds);
                setIsFetchDisabled(true);
            } else {
                localStorage.removeItem(FETCH_NEW_DATA_DISABLE_UNTIL_KEY);
                setIsFetchDisabled(false);
                setFetchTimeLeft(0);
            }
        }

        let timer;
        if (isFetchDisabled && fetchTimeLeft > 0) {
            timer = setInterval(() => {
                setFetchTimeLeft((prevTime) => {
                    const newTime = prevTime - 1;
                    if (newTime <= 0) {
                        localStorage.removeItem(FETCH_NEW_DATA_DISABLE_UNTIL_KEY);
                        setIsFetchDisabled(false);
                        return 0;
                    }
                    return newTime;
                });
            }, 1000);
        }

        return () => clearInterval(timer);
    }, [isFetchDisabled, fetchTimeLeft]);

    const formatFetchTimeLeft = () => {
        const minutes = Math.floor(fetchTimeLeft / 60);
        const seconds = fetchTimeLeft % 60;
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };

    const startFetchNewDataCooldown = () => {
        const disableUntilTimestamp = Date.now() + FETCH_NEW_DATA_COOLDOWN_MS;
        localStorage.setItem(FETCH_NEW_DATA_DISABLE_UNTIL_KEY, disableUntilTimestamp.toString());
        setIsFetchDisabled(true);
        setFetchTimeLeft(FETCH_NEW_DATA_COOLDOWN_SECONDS);
    };

    const handleSearchSubmit = () => {
        const { startDate: nextStartDate, endDate: nextEndDate } = resolveDateRange(startDate, endDate);
        if (moment(nextEndDate).isBefore(moment(nextStartDate))) {
            props.setSnackInfo('End date cannot be before start date', 'warning');
            return;
        }

        setStartDate(nextStartDate);
        setEndDate(nextEndDate);
        setAppliedStartDate(nextStartDate);
        setAppliedEndDate(nextEndDate);

        const searchText = search.trim();
        setActiveSearch(searchText);
        fetchBothTabData(searchText, {
            startDate: nextStartDate,
            endDate: nextEndDate,
        }, { updateModularCache: !searchText });
    };

    const handleClearFilter = () => {
        const defaults = getDefaultDateRange();
        setSearch('');
        setActiveSearch('');
        setActiveSearchMode(null);
        setStartDate(defaults.startDate);
        setEndDate(defaults.endDate);
        setAppliedStartDate(defaults.startDate);
        setAppliedEndDate(defaults.endDate);
        fetchBothTabData('', {
            startDate: defaults.startDate,
            endDate: defaults.endDate,
        }, { updateModularCache: true });
    };

    const handleFetchNewData = () => {
        if (isFetchDisabled || activeTab !== SNAG_TAB_MODULAR) return;
        const defaults = getDefaultDateRange();
        setSearch('');
        setActiveSearch('');
        setActiveSearchMode(null);
        setStartDate(defaults.startDate);
        setEndDate(defaults.endDate);
        setAppliedStartDate(defaults.startDate);
        setAppliedEndDate(defaults.endDate);
        startFetchNewDataCooldown();
        fetchModularData('', {
            startDate: defaults.startDate,
            endDate: defaults.endDate,
            updateCache: true,
        });
    };

    const handleTabChange = (_, value) => {
        setActiveTab(value);
    };

    const handleSearchKeyDown = (event) => {
        if (event.key === 'Enter') {
            handleSearchSubmit();
        }
    };

    const totals = rows.reduce((acc, row) => {
        STAGE_COLUMNS.forEach((col) => {
            acc[col.key] += col.getCount(row);
        });
        return acc;
    }, STAGE_COLUMNS.reduce((acc, col) => {
        acc[col.key] = 0;
        return acc;
    }, {}));

    const hardwareTotals = hardwareRows.reduce((acc, row) => {
        HARDWARE_QUEUE_COLUMNS.forEach((col) => {
            acc[col.key] += Number(row[col.key] || 0);
        });
        return acc;
    }, HARDWARE_QUEUE_COLUMNS.reduce((acc, col) => {
        acc[col.key] = 0;
        return acc;
    }, {}));

    const pageTitle = activeTab === SNAG_TAB_HARDWARE
        ? 'Hardware Snag Details'
        : 'Modular Snag Details';

    const openPanelDetails = async (row, column) => {
        const count = column.getCount(row);
        if (!count) return;

        props.setLoading(true);
        try {
            const res = await HttpService.getSnagPanelDetails(buildSnagPanelPayload({
                projectId: row._id,
                projectNo: row.project_no,
                stage: column.stage,
                count,
                limit: PANEL_PREVIEW_LIMIT,
            }, activeSearch, activeSearchMode));
            const panels = res.data?.msg || [];
            setPanelRows(panels);
            setPanelMeta({
                projectNo: row.project_no,
                stageLabel: column.label,
                stage: column.stage,
                count: res.data?.count != null ? res.data.count : panels.length,
                previewCount: res.data?.previewCount != null ? res.data.previewCount : panels.length,
                isPreview: !!res.data?.isPreview,
                projectId: row._id,
                projectIds: [],
                barcode: activeSearchMode === 'barcode' ? activeSearch.trim() : '',
            });
            setPanelDialogOpen(true);
        } catch (error) {
            props.setSnackInfo(
                error?.response?.data?.message || error?.response?.data?.error || 'Failed to load panel details',
                'error'
            );
        }
        props.setLoading(false);
    };

    const openTotalPanelDetails = async (column) => {
        const count = totals[column.key];
        if (!count || !rows.length) return;

        props.setLoading(true);
        try {
            const res = await HttpService.getSnagPanelDetails(buildSnagPanelPayload({
                projectIds: rows.map((row) => row._id),
                stage: column.stage,
                count,
                limit: PANEL_PREVIEW_LIMIT,
            }, activeSearch, activeSearchMode));
            const panels = res.data?.msg || [];
            setPanelRows(panels);
            setPanelMeta({
                projectNo: `All Projects (${rows.length})`,
                stageLabel: column.label,
                stage: column.stage,
                count: res.data?.count != null ? res.data.count : panels.length,
                previewCount: res.data?.previewCount != null ? res.data.previewCount : panels.length,
                isPreview: !!res.data?.isPreview,
                projectId: '',
                projectIds: rows.map((row) => row._id),
                barcode: activeSearchMode === 'barcode' ? activeSearch.trim() : '',
            });
            setPanelDialogOpen(true);
        } catch (error) {
            props.setSnackInfo(
                error?.response?.data?.message || error?.response?.data?.error || 'Failed to load panel details',
                'error'
            );
        }
        props.setLoading(false);
    };

    const flattenHardwareItemsForBucket = (snags = [], bucket, projectNo = '') => {
        const list = [];
        (snags || []).forEach((snag, snagIndex) => {
            (snag.items || []).forEach((item) => {
                if (classifyHardwareItemBucket(item) !== bucket) return;
                list.push({
                    projectNo: projectNo || snag.projectNo || '—',
                    snagIndex: snagIndex + 1,
                    pieceName: item.pieceName || item.description || '—',
                    qty: item.qty ?? item.quantity ?? '—',
                    status: item.status || '—',
                    reason: item.reason || item.reasonForChange || '—',
                    assignedTo: resolveHardwareItemAssignee(item, snag, bucket),
                    updatedAt: snag.updatedAt || snag.createdAt,
                });
            });
        });
        return list;
    };

    const openHardwareQueueDetails = async (row, column) => {
        const count = Number(row[column.key] || 0);
        if (!count) return;

        props.setLoading(true);
        try {
            const res = await HttpService.getHardwareSnagProjectDetails(row.projectNo);
            const snags = filterSnagsByCreatedDate(res.data?.snags || [], appliedStartDate, appliedEndDate);
            setHardwareDetailRows(flattenHardwareItemsForBucket(snags, column.bucket, row.projectNo));
            setHardwareDialogMeta({
                projectNo: row.projectNo,
                customerName: row.customerName || res.data?.customerName || '—',
                city: row.city || res.data?.city || '—',
                stageLabel: column.label,
                bucket: column.bucket,
            });
            setHardwareDialogOpen(true);
        } catch (error) {
            props.setSnackInfo(
                error?.response?.data?.message || 'Failed to load hardware snag details',
                'error'
            );
        }
        props.setLoading(false);
    };

    const openHardwareTotalQueueDetails = async (column) => {
        const count = hardwareTotals[column.key];
        if (!count || !hardwareRows.length) return;

        props.setLoading(true);
        try {
            const allItems = [];
            for (const row of hardwareRows) {
                if (!Number(row[column.key] || 0)) continue;
                const res = await HttpService.getHardwareSnagProjectDetails(row.projectNo);
                const snags = filterSnagsByCreatedDate(res.data?.snags || [], appliedStartDate, appliedEndDate);
                flattenHardwareItemsForBucket(snags, column.bucket, row.projectNo).forEach((item) => {
                    allItems.push(item);
                });
            }
            setHardwareDetailRows(allItems);
            setHardwareDialogMeta({
                projectNo: `All Projects (${hardwareRows.length})`,
                customerName: '—',
                city: '—',
                stageLabel: column.label,
                bucket: column.bucket,
            });
            setHardwareDialogOpen(true);
        } catch (error) {
            props.setSnackInfo(
                error?.response?.data?.message || 'Failed to load hardware snag details',
                'error'
            );
        }
        props.setLoading(false);
    };

    const renderHardwareCountCell = (row, column) => {
        const count = Number(row[column.key] || 0);
        if (!count) {
            return <span className={classes.disabledCount}>0</span>;
        }
        return (
            <span
                className={classes.clickableCount}
                onClick={() => openHardwareQueueDetails(row, column)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') openHardwareQueueDetails(row, column);
                }}
            >
                {count}
            </span>
        );
    };

    const renderHardwareTotalCountCell = (column) => {
        const count = hardwareTotals[column.key];
        if (!count) {
            return <span className={classes.disabledCount}>0</span>;
        }
        return (
            <span
                className={classes.clickableCount}
                onClick={() => openHardwareTotalQueueDetails(column)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') openHardwareTotalQueueDetails(column);
                }}
            >
                {count}
            </span>
        );
    };

    const downloadSummaryCsv = () => {
        const startLabel = moment(appliedStartDate).format('DD-MM-YYYY');
        const endLabel = moment(appliedEndDate).format('DD-MM-YYYY');

        if (activeTab === SNAG_TAB_HARDWARE) {
            if (!hardwareRows.length) {
                props.setSnackInfo('No data to download for selected date range', 'warning');
                return;
            }
            const csvRows = hardwareRows.map((row, index) => ({
                SN: index + 1,
                'Project No': row.projectNo || '',
                'Customer Name': row.customerName || '',
                City: row.city || '',
                'Created Date': formatCreatedAt(row.createdDate),
                'City Ops Manager': row.cityOps || 0,
                'Center Assistant Director': row.centralDirector || 0,
                Finance: row.finance || 0,
                Store: row.store || 0,
            }));
            csvRows.push({
                SN: '',
                'Project No': `Total (${hardwareRows.length})`,
                'Customer Name': '',
                City: '',
                'Created Date': '',
                'City Ops Manager': hardwareTotals.cityOps,
                'Center Assistant Director': hardwareTotals.centralDirector,
                Finance: hardwareTotals.finance,
                Store: hardwareTotals.store,
            });
            const worksheet = XLSX.utils.json_to_sheet(csvRows);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Hardware Snag Details');
            XLSX.writeFile(workbook, `Hardware_Snag_Details_${startLabel}_to_${endLabel}.csv`, { bookType: 'csv' });
            return;
        }

        if (!rows.length) {
            props.setSnackInfo('No data to download for selected date range', 'warning');
            return;
        }

        const csvRows = rows.map((row, index) => ({
            SN: index + 1,
            'Project No': row.project_no || '',
            'Created Date': formatCreatedAt(row.createdAt || row.createdDate),
            'Beam Saw': stageValue(row, 'Beam saw'),
            'Edge Banding': stageValue(row, 'Edge banding'),
            CNC: stageValue(row, 'CNC'),
            'Alu Panel': stageValue(row, 'Alu Panels'),
            'CNC Jali': stageValue(row, 'CNC Jali'),
            'Custom Panel': stageValue(row, 'Custom Panels'),
            QC: stageValue(row, 'Assembly'),
            Packaging: stageValue(row, 'Packaging'),
            'Ready for Dispatch': stageValue(row, 'Completed'),
            Dispatched: stageValue(row, 'Delivered'),
            'Total Panels': Number(row.totalPanels || 0),
        }));

        csvRows.push({
            SN: '',
            'Project No': `Total (${rows.length})`,
            'Created Date': '',
            'Beam Saw': totals.beamSaw,
            'Edge Banding': totals.edgeBanding,
            CNC: totals.cnc,
            'Alu Panel': totals.aluPanel,
            'CNC Jali': totals.cncJali,
            'Custom Panel': totals.customPanel,
            QC: totals.qc,
            Packaging: totals.packaging,
            'Ready for Dispatch': totals.readyForDispatch,
            Dispatched: totals.deliveredProductPanel,
            'Total Panels': totals.totalPanels,
        });

        const worksheet = XLSX.utils.json_to_sheet(csvRows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Modular Snag Details');
        XLSX.writeFile(workbook, `Modular_Snag_Details_${startLabel}_to_${endLabel}.csv`, { bookType: 'csv' });
    };

    const downloadPanelExcel = async () => {
        if (!panelMeta.count) {
            props.setSnackInfo('No panel data to download', 'warning');
            return;
        }

        const fileName = `${panelMeta.projectNo || 'Snag'}_${panelMeta.stageLabel || panelMeta.stage}_panels.csv`;
        const payload = {
            stage: panelMeta.stage,
            fileName,
        };

        if (panelMeta.barcode) {
            payload.barcode = panelMeta.barcode;
        }

        if (panelMeta.projectIds?.length) {
            payload.projectIds = panelMeta.projectIds;
        } else if (panelMeta.projectId) {
            payload.projectId = panelMeta.projectId;
        } else {
            props.setSnackInfo('Missing project details for download', 'error');
            return;
        }

        setPanelDownloadProgress(0);
        setPanelDownloadOpen(true);

        try {
            setPanelDownloadProgress(15);
            const fetchPayload = buildSnagPanelPayload({
                ...payload,
                count: panelMeta.count,
                limit: panelMeta.count,
            }, activeSearch, activeSearchMode);

            const res = await HttpService.getSnagPanelDetails(fetchPayload);
            setPanelDownloadProgress(75);
            const panels = res.data?.msg || [];
            exportSnagPanelsToCsv(panels, fileName, panelMeta.stage);
            setPanelDownloadProgress(100);
            props.setSnackInfo(`Downloaded ${panels.length.toLocaleString()} panel records`, 'success');
        } catch (error) {
            props.setSnackInfo(
                error?.response?.data?.message || error?.response?.data?.error || 'Failed to download panel details',
                'error'
            );
        } finally {
            setPanelDownloadOpen(false);
            setPanelDownloadProgress(0);
        }
    };

    const renderCountCell = (row, column) => {
        const count = column.getCount(row);
        if (!count) {
            return <span className={classes.disabledCount}>0</span>;
        }
        return (
            <span
                className={classes.clickableCount}
                onClick={() => openPanelDetails(row, column)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') openPanelDetails(row, column);
                }}
            >
                {count}
            </span>
        );
    };

    const renderTotalCountCell = (column) => {
        const count = totals[column.key];
        if (!count) {
            return <span className={classes.disabledCount}>0</span>;
        }
        return (
            <span
                className={classes.clickableCount}
                onClick={() => openTotalPanelDetails(column)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') openTotalPanelDetails(column);
                }}
            >
                {count}
            </span>
        );
    };

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <div>
                    <Typography variant="h5" className={classes.title}>{pageTitle}</Typography>
                    <Typography variant="body2" color="textSecondary">
                        {activeTab === SNAG_TAB_HARDWARE
                            ? 'Search by project or customer, filter by snag created date range'
                            : 'Search by project number or barcode, filter by created date range'}
                    </Typography>
                </div>
                <Box display="flex" style={{ gap: 8 }} alignItems="center" flexWrap="wrap">
                    <TextField
                        className={classes.search}
                        size="small"
                        variant="outlined"
                        label="Search project / barcode"
                        placeholder="Project no or barcode"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                    />
                    <TextField
                        className={classes.dateField}
                        size="small"
                        variant="outlined"
                        label="Start Date"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        className={classes.dateField}
                        size="small"
                        variant="outlined"
                        label="End Date"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <Button
                        className={classes.actionBtn}
                        variant="contained"
                        onClick={handleSearchSubmit}
                    >
                        Submit
                    </Button>
                    <Button
                        className={classes.actionBtn}
                        variant="contained"
                        onClick={handleClearFilter}
                    >
                        Clear Filter
                    </Button>
                    <Button
                        className={isFetchDisabled ? classes.disabledActionBtn : classes.actionBtn}
                        variant="contained"
                        onClick={handleFetchNewData}
                        disabled={isFetchDisabled}
                    >
                        {isFetchDisabled ? `Wait (${formatFetchTimeLeft()})` : 'Fetch New Data'}
                    </Button>
                    <AntButton
                        type="primary"
                        icon={<DownloadOutlined />}
                        disabled={activeTab === SNAG_TAB_HARDWARE ? !hardwareRows.length : !rows.length}
                        onClick={downloadSummaryCsv}
                        style={{
                            backgroundColor: GREEN,
                            color: '#FFF2EB',
                            height: 40,
                            padding: '0 16px',
                            border: 'none',
                            fontWeight: 600,
                            display: 'inline-flex',
                            alignItems: 'center',
                        }}
                    >
                        Download CSV
                    </AntButton>
                </Box>
            </div>

            <Tabs
                value={activeTab}
                onChange={handleTabChange}
                className={classes.tabsRoot}
                indicatorColor="primary"
                textColor="primary"
            >
                <Tab className={classes.tab} value={SNAG_TAB_MODULAR} label="Modular" />
                <Tab className={classes.tab} value={SNAG_TAB_HARDWARE} label="Hardware Snag" />
            </Tabs>

            {activeTab === SNAG_TAB_MODULAR && (
            <TableContainer component={Paper} className={classes.tableContainer}>
                <Table className={classes.table} size="small">
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>SN</StyledTableCell>
                            <StyledTableCell>Project No</StyledTableCell>
                            <StyledTableCell>Created Date</StyledTableCell>
                            {STAGE_COLUMNS.map((col) => (
                                <StyledTableCell key={col.key}>{col.label}</StyledTableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, index) => (
                            <TableRow key={row._id || row.project_no}>
                                <StyledTableCell>{index + 1}</StyledTableCell>
                                <StyledTableCell>{row.project_no}</StyledTableCell>
                                <StyledTableCell>{formatCreatedAt(row.createdAt || row.createdDate)}</StyledTableCell>
                                {STAGE_COLUMNS.map((col) => (
                                    <StyledTableCell key={col.key}>
                                        {renderCountCell(row, col)}
                                    </StyledTableCell>
                                ))}
                            </TableRow>
                        ))}

                        {rows.length > 0 && (
                            <TableRow className={classes.totalRow}>
                                <StyledTableCell colSpan={3}>Total ({rows.length})</StyledTableCell>
                                {STAGE_COLUMNS.map((col) => (
                                    <StyledTableCell key={col.key}>
                                        {renderTotalCountCell(col)}
                                    </StyledTableCell>
                                ))}
                            </TableRow>
                        )}

                        {!rows.length && (
                            <TableRow>
                                <StyledTableCell colSpan={3 + STAGE_COLUMNS.length}>
                                    {activeSearch ? 'Snag not Exist' : 'No snag projects found for selected date range.'}
                                </StyledTableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            )}

            {activeTab === SNAG_TAB_HARDWARE && (
            <TableContainer component={Paper} className={classes.tableContainer}>
                <Table className={classes.table} size="small">
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>SN</StyledTableCell>
                            <StyledTableCell>Project No</StyledTableCell>
                            <StyledTableCell>Customer Name</StyledTableCell>
                            <StyledTableCell>City</StyledTableCell>
                            <StyledTableCell>Created Date</StyledTableCell>
                            {HARDWARE_QUEUE_COLUMNS.map((col) => (
                                <StyledTableCell key={col.key}>{col.label}</StyledTableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {hardwareRows.map((row, index) => (
                            <TableRow key={row.projectNo || index}>
                                <StyledTableCell>{index + 1}</StyledTableCell>
                                <StyledTableCell>{row.projectNo}</StyledTableCell>
                                <StyledTableCell>{row.customerName || '—'}</StyledTableCell>
                                <StyledTableCell>{row.city || '—'}</StyledTableCell>
                                <StyledTableCell>{formatCreatedAt(row.createdDate)}</StyledTableCell>
                                {HARDWARE_QUEUE_COLUMNS.map((col) => (
                                    <StyledTableCell key={col.key}>
                                        {renderHardwareCountCell(row, col)}
                                    </StyledTableCell>
                                ))}
                            </TableRow>
                        ))}

                        {hardwareRows.length > 0 && (
                            <TableRow className={classes.totalRow}>
                                <StyledTableCell colSpan={5}>Total ({hardwareRows.length})</StyledTableCell>
                                {HARDWARE_QUEUE_COLUMNS.map((col) => (
                                    <StyledTableCell key={col.key}>
                                        {renderHardwareTotalCountCell(col)}
                                    </StyledTableCell>
                                ))}
                            </TableRow>
                        )}

                        {!hardwareRows.length && (
                            <TableRow>
                                <StyledTableCell colSpan={5 + HARDWARE_QUEUE_COLUMNS.length}>
                                    {activeSearch ? 'Snag not Exist' : 'No hardware snag projects found for selected date range.'}
                                </StyledTableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            )}

            <Dialog
                open={panelDialogOpen}
                onClose={() => setPanelDialogOpen(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle className={classes.dialogTitle}>
                    Panel Details — {panelMeta.projectNo} ({panelMeta.stageLabel})
                </DialogTitle>
                <DialogContent dividers>
                    <Typography className={classes.dialogMeta} variant="body2">
                        Stage: <strong>{panelMeta.stageLabel}</strong>
                        {' | '}
                        Panels: <strong>{panelMeta.count.toLocaleString()}</strong>
                        {panelMeta.isPreview && (
                            <>
                                {' | '}
                                Showing first <strong>{panelMeta.previewCount.toLocaleString()}</strong> in table.
                                {' '}
                                Use download for all records.
                            </>
                        )}
                    </Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {getPanelColumnsForStage(panelMeta.stage).map((col) => (
                                        <StyledTableCell key={col.key}>{col.label}</StyledTableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {panelRows.map((panel, index) => (
                                    <TableRow key={panel._id || `${panel.barcode}-${index}`}>
                                        {getPanelColumnsForStage(panelMeta.stage).map((col) => (
                                            <StyledTableCell key={col.key}>
                                                {getPanelCellValue(panel, col, index)}
                                            </StyledTableCell>
                                        ))}
                                    </TableRow>
                                ))}
                                {!panelRows.length && (
                                    <TableRow>
                                        <StyledTableCell colSpan={getPanelColumnsForStage(panelMeta.stage).length}>
                                            No panels found for this stage.
                                        </StyledTableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPanelDialogOpen(false)}>Close</Button>
                    <Button
                        className={classes.downloadBtn}
                        variant="contained"
                        onClick={downloadPanelExcel}
                        disabled={!panelMeta.count || panelDownloadOpen}
                    >
                        {panelMeta.isPreview ? 'Download All CSV' : 'Download CSV'}
                    </Button>
                </DialogActions>
            </Dialog>

            <PanelDownloadProgressDialog
                open={panelDownloadOpen}
                progress={panelDownloadProgress}
                totalCount={panelMeta.count}
            />

            <Dialog
                open={hardwareDialogOpen}
                onClose={() => setHardwareDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle className={classes.dialogTitle}>
                    Hardware Snag Items — {hardwareDialogMeta.projectNo} ({hardwareDialogMeta.stageLabel})
                </DialogTitle>
                <DialogContent dividers>
                    <Typography className={classes.dialogMeta} variant="body2">
                        Customer: <strong>{hardwareDialogMeta.customerName}</strong>
                        {' | '}
                        City: <strong>{hardwareDialogMeta.city}</strong>
                        {' | '}
                        Queue: <strong>{getHardwareStageLabel(hardwareDialogMeta.bucket)}</strong>
                        {' | '}
                        Items: <strong>{hardwareDetailRows.length}</strong>
                    </Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell>SN</StyledTableCell>
                                    {hardwareDialogMeta.projectNo.startsWith('All Projects') && (
                                        <StyledTableCell>Project No</StyledTableCell>
                                    )}
                                    <StyledTableCell>Item</StyledTableCell>
                                    <StyledTableCell>Qty</StyledTableCell>
                                    <StyledTableCell>Status</StyledTableCell>
                                    <StyledTableCell>Assign To</StyledTableCell>
                                    <StyledTableCell>Reason</StyledTableCell>
                                    <StyledTableCell>Updated</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {hardwareDetailRows.map((item, index) => (
                                    <TableRow key={`${item.projectNo || ''}-${item.snagIndex}-${index}`}>
                                        <StyledTableCell>{index + 1}</StyledTableCell>
                                        {hardwareDialogMeta.projectNo.startsWith('All Projects') && (
                                            <StyledTableCell>{item.projectNo || '—'}</StyledTableCell>
                                        )}
                                        <StyledTableCell>{item.pieceName}</StyledTableCell>
                                        <StyledTableCell>{item.qty}</StyledTableCell>
                                        <StyledTableCell>{item.status}</StyledTableCell>
                                        <StyledTableCell>{item.assignedTo}</StyledTableCell>
                                        <StyledTableCell>{item.reason}</StyledTableCell>
                                        <StyledTableCell>{formatCreatedAt(item.updatedAt)}</StyledTableCell>
                                    </TableRow>
                                ))}
                                {!hardwareDetailRows.length && (
                                    <TableRow>
                                        <StyledTableCell
                                            colSpan={
                                                hardwareDialogMeta.projectNo.startsWith('All Projects') ? 8 : 7
                                            }
                                        >
                                            No items in this queue.
                                        </StyledTableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHardwareDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

const mapStateToProps = (state) => ({
    snagProjects: state.snagDetails.projects,
    isSnagProjectsLoaded: state.snagDetails.isLoaded,
});

const mapDispatchToProps = (dispatch) => ({
    setLoading: (value) => dispatch(setLoading(value)),
    setSnackInfo: (msg, severity) => dispatch(setSnackInfo(msg, severity)),
    setSnagProjects: (projects) => dispatch(setSnagProjects(projects)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SnagDetails);